import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { flushSync } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { CompanyService } from "./company.service";
import CollapseHeader from "../../../core/common/collapse-header";
import CompanyForm from "./CompanyForm";
import "./CompanyForm.css";
import { Modal, OverlayTrigger, Tooltip as BootstrapTooltip } from "react-bootstrap";
import {
  showSuccessAlert,
  showErrorAlert,
  showConfirmationAlert,
} from "../../uiInterface/base-ui/alertservice";
import { AuthContext } from "../../../context/authcontext";
import TableFilters, {
  FilterState,
  FilterConfig,
} from "../../../components/filters/TableFilters";
import TablePagination from "../../../components/pagination/TablePagination";
import { handleAuthError, getAuthHeaders } from "../../../utils/authUtils";
import companyLogo from "../../../assets/company.png";
import ExportComponent from "../../../components/ExportComponent";
import Tooltip from "@mui/material/Tooltip";
import CompanyContactFilterDropdown from "../components/CompanyContactFilterDropdown";

const spinningStyles = `
  .ti-spin {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }


`;

if (typeof document !== "undefined") {
  const styleElement = document.createElement("style");
  styleElement.textContent = spinningStyles;
  if (!document.head.querySelector("style[data-spinning]")) {
    styleElement.setAttribute("data-spinning", "true");
    document.head.appendChild(styleElement);
  }
}

const Preloader = () => {
  return (
    <div className="preloader">
      <div className="preloader">
        <div className="loader"></div>
      </div>
    </div>
  );
};

interface CompaniesGridViewProps {
  companies: any[];
  loading: boolean;
  error: string | null;
  pagination: any;
  fetchCompanies: (page?: number) => Promise<void>;
  refreshCompanies: () => Promise<void>;
  addCompany: (company: any) => void;
  updateCompany: (company: any) => void;
  removeCompany: (companyId: number) => void;
  lastFetched: Date | null;
  isInitialized: boolean;
  currentView: "list" | "grid";
  onViewSwitch: (view: "list" | "grid") => void;
}

const CompaniesGridView = ({
  companies,
  loading,
  error,
  pagination,
  fetchCompanies,
  refreshCompanies,
  addCompany,
  updateCompany,
  removeCompany,
  currentView,
  onViewSwitch,
  isInitialized,
  ...rest
}: CompaniesGridViewProps) => {
  const route = all_routes;
  const navigate = useNavigate();
  const { selectedTenantId, auth } = useContext(AuthContext);

  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [isAddCompanyOpen, setIsAddCompanyOpen] = useState(false);
  const [isEditCompanyOpen, setIsEditCompanyOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formResetTrigger, setFormResetTrigger] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [deleteError, setDeleteError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);

  const [filters, setFilters] = useState<FilterState>({
    searchTerm: "",
    dateRange: [null, null] as [Date | null, Date | null],
    industryFilter: [],
    countryFilter: [],
    statusFilter: null, // Add statusFilter property
    sortBy: "created_datetime",
    sortOrder: "desc",
  });

  const [additionalFilters, setAdditionalFilters] = useState({
    teamFilter: null as number | null,
    userFilter: null as number | null,
    sourceFilter: null as string | null,
  });

  const [filteredCompaniesData, setFilteredCompaniesData] = useState<any[]>([]);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [isFilterLoading, setIsFilterLoading] = useState(false);

  useEffect(() => {
    if (pagination.currentPage && pagination.currentPage !== currentPage) {
      setCurrentPage(pagination.currentPage);
    }
  }, [pagination.currentPage, currentPage]);

  const filterConfig: FilterConfig = useMemo(
    () => ({
      showSearch: true,
      searchPlaceholder: "Search companies...",
      searchField: "company_name",
      showDateFilter: true,
      dateField: "created_datetime",
      showIndustryFilter: true,
      industryField: "industry_type_display_name",
      showCountryFilter: true,
      countryField: "country",
      pageName: "company" as const,
      defaultSort: {
        field: "created_datetime",
        order: "desc" as const,
      },
    }),
    []
  );

  const handleFilterChange = useCallback((newFilters: FilterState) => {
    setFilters(newFilters);
    setCurrentPage(1);

    if (
      !newFilters.searchTerm &&
      !newFilters.dateRange[0] &&
      !newFilters.dateRange[1]
    ) {
      setIsFilterActive(false);
      setFilteredCompaniesData([]);
    }
  }, []);

  const handleApiFilterChange = useCallback(
    (apiFilters: Record<string, any>) => { },
    []
  );

  const handleFiltersReset = useCallback(() => {
    setIsFilterActive(false);
    setFilteredCompaniesData([]);
    setFilters({
      searchTerm: "",
      dateRange: [null, null] as [Date | null, Date | null],
      industryFilter: [],
      countryFilter: [],
      statusFilter: null, // Add statusFilter property
      sortBy: "created_datetime",
      sortOrder: "desc",
    });
    setCurrentPage(1);
    fetchCompanies(1);
  }, [fetchCompanies]);

  const handleApiCall = useCallback(
    async (url: string) => {
      if (!selectedTenantId) {
        return;
      }

      try {
        setIsFilterLoading(true);

        const headers = getAuthHeaders();

        const response = await fetch(url, {
          method: "GET",
          headers: headers,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.results && Array.isArray(data.results)) {
          setFilteredCompaniesData(data.results);
          setIsFilterActive(true);
        } else {
          setFilteredCompaniesData([]);
          setIsFilterActive(true);
        }
      } catch (error: any) {
        try {
          handleAuthError(error);
        } catch (authError) {
          return;
        }

        setFilteredCompaniesData([]);
        setIsFilterActive(false);
      } finally {
        setIsFilterLoading(false);
      }
    },
    [selectedTenantId]
  );

  useEffect(() => {
    return () => {
      document.querySelectorAll(".offcanvas-backdrop, .modal-backdrop").forEach((el) => el.remove());
      document.body.classList.remove("modal-open", "offcanvas-open");
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, []);

  const closeOffcanvas = (offcanvasId: string) => {
    const modal = document.getElementById(offcanvasId);
    if (modal) {
      const bsOffcanvas = (window as any).bootstrap?.Offcanvas?.getInstance(modal);
      if (bsOffcanvas) {
        bsOffcanvas.hide();
      } else {
        const closeButton = modal.querySelector('[data-bs-dismiss="offcanvas"]') as HTMLElement;
        if (closeButton) closeButton.click();
      }
    }
  };

  useEffect(() => {
    const addOffcanvas = document.getElementById("add_company");

    const handleHidden = () => {
      setIsAddCompanyOpen(false);
    };

    if (addOffcanvas) {
      addOffcanvas.addEventListener("hidden.bs.offcanvas", handleHidden);
    }

    return () => {
      if (addOffcanvas) {
        addOffcanvas.removeEventListener("hidden.bs.offcanvas", handleHidden);
      }
    };
  }, []);

  useEffect(() => {
    const editOffcanvas = document.getElementById("offcanvas_edit");

    const handleHidden = () => {
      setIsEditCompanyOpen(false);
    };

    if (editOffcanvas) {
      editOffcanvas.addEventListener("hidden.bs.offcanvas", handleHidden);
    }

    return () => {
      if (editOffcanvas) {
        editOffcanvas.removeEventListener("hidden.bs.offcanvas", handleHidden);
      }
    };
  }, []);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchCompanies(page);
  };

  const { filteredCompanies, totalFilteredCount } = useMemo(() => {
    if (isFilterActive) {
      return {
        filteredCompanies: filteredCompaniesData,
        totalFilteredCount: filteredCompaniesData.length,
      };
    }

    if (!Array.isArray(companies)) {
      return { filteredCompanies: [], totalFilteredCount: 0 };
    }

    return {
      filteredCompanies: companies,
      totalFilteredCount: pagination.totalCount,
    };
  }, [companies, filteredCompaniesData, isFilterActive, pagination.totalCount]);

  const displayCompanies = useMemo(() => {
    if (isFilterActive) {
      const startIndex = (currentPage - 1) * 12;
      const endIndex = startIndex + 12;
      return filteredCompanies.slice(startIndex, endIndex);
    }
    return filteredCompanies;
  }, [filteredCompanies, isFilterActive, currentPage]);

  const handleEdit = (company: any) => {
    flushSync(() => {
      setSelectedCompany(company);
      setIsEditCompanyOpen(true);
    });
  };

  const handleCreate = async (company: any) => {
    setIsCreating(true);
    setIsSubmitting(true);
    try {
      const logoFile = company.logoFile;
      delete company.logoFile;

      let newCompany;
      if (logoFile) {
        const uploadFormData = new FormData();
        Object.entries(company).forEach(([k, v]) =>
          uploadFormData.append(k, String(v))
        );
        uploadFormData.append("company_logo", logoFile);

        newCompany = await CompanyService.createCompanyWithLogo(uploadFormData);
      } else {
        newCompany = await CompanyService.createCompany(company);
      }

      await addCompany(newCompany);

      showSuccessAlert(
        `Company "${newCompany.company_name}" created successfully!`
      );

      setSelectedCompany(null);
      setFormResetTrigger((prev) => prev + 1);

      setTimeout(() => {
        closeOffcanvas("add_company");
      }, 100);

      setTimeout(() => {
        fetchCompanies(currentPage);
      }, 500);
    } catch (err) {
      console.error("Failed to create company:", err);
      showErrorAlert("Failed to create company");

      setTimeout(() => {
        closeOffcanvas("add_company");
      }, 100);
      throw err;
    } finally {
      setIsCreating(false);
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async (id: number, company: any) => {
    setIsUpdating(true);
    setIsSubmitting(true);
    try {
      const logoFile = company.logoFile;
      delete company.logoFile;

      let updatedCompany;
      if (logoFile) {
        const uploadFormData = new FormData();
        Object.entries(company).forEach(([k, v]) =>
          uploadFormData.append(k, String(v))
        );
        uploadFormData.append("company_logo", logoFile);

        updatedCompany = await CompanyService.updateCompanyWithLogo(
          id,
          uploadFormData
        );
      } else {
        updatedCompany = await CompanyService.updateCompany(id, company);
      }

      updateCompany(updatedCompany);

      showSuccessAlert(
        `Company "${updatedCompany.company_name}" updated successfully!`
      );

      setTimeout(() => {
        closeOffcanvas("offcanvas_edit");
      }, 100);

      setTimeout(() => {
        fetchCompanies(currentPage);
      }, 500);

      setSelectedCompany(null);
    } catch (err) {
      console.error("Failed to update company:", err);
      showErrorAlert("Failed to update company");

      setTimeout(() => {
        closeOffcanvas("offcanvas_edit");
      }, 100);
      throw err;
    } finally {
      setIsUpdating(false);
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = (formData: any) => {
    if (selectedCompany) {
      return handleUpdate(selectedCompany.company_id, formData);
    } else {
      return handleCreate(formData);
    }
  };

  const handleDelete = async (company: any) => {
    const result = await showConfirmationAlert(
      "Delete Company",
      `Are you sure you want to delete "${company.company_name}"? This action cannot be undone.`,
      async () => {
        setIsDeleting(true);
        setIsLoading(true);
        try {
          await CompanyService.deleteCompany(company.company_id);
        } finally {
          setIsDeleting(false);
          setIsLoading(false);
        }
      }
    );

    if (result.isConfirmed && result.value) {
      removeCompany(company.company_id);

      showSuccessAlert(
        `Company "${company.company_name}" deleted successfully!`
      );

      setSelectedCompany(null);
      fetchCompanies(currentPage);
    }
  };

  const createCompanyUrl = (company: any): string => {
    return `/companies/${company.company_id}`;
  };

  const handlePreview = (company: any) => {
    try {
      setIsPreviewing(true);

      const dropdowns = document.querySelectorAll(".dropdown-menu.show");
      dropdowns.forEach((dropdown) => {
        dropdown.classList.remove("show");
      });

      const companyUrl = createCompanyUrl(company);
      navigate(companyUrl);
    } catch (error) {
      console.error("Error navigating to company details:", error);

      const companyUrl = createCompanyUrl(company);
      window.location.href = companyUrl;
    } finally {
      setIsPreviewing(false);
    }
  };

  const formatLocation = (city: string, country: string): string => {
    const locationParts = [city, country].filter(
      (part) => part && part.trim() !== ""
    );
    return locationParts.length > 0 ? locationParts.join(", ") : "";
  };

  const hasAddress = (address: string | null | undefined): boolean => {
    return Boolean(
      address &&
      address.trim() !== "" &&
      address.trim() !== "N/A" &&
      address.trim() !== "null"
    );
  };

  const hasState = (state: string | null | undefined): boolean => {
    return Boolean(
      state &&
      state.trim() !== "" &&
      state.trim() !== "N/A" &&
      state.trim() !== "null"
    );
  };

  const renderCompanyCard = (company: any) => (
    <div key={company.company_id} className="col-xxl-3 col-xl-4 col-md-6">
      <div className="card border shadow">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center">
              <Link
                to={createCompanyUrl(company)}
                className="avatar avatar-md flex-shrink-0 me-2"
              >
                {company.company_logo ? (
                  <img
                    src={company.company_logo}
                    alt={`${company.company_name} logo`}
                    className="rounded-circle"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                      const parent = target.parentElement;
                      if (parent && !parent.querySelector(".fallback-icon")) {
                        const fallbackDiv = document.createElement("div");
                        fallbackDiv.className =
                          "d-flex align-items-center justify-content-center w-100 h-100 bg-light rounded-circle fallback-icon";
                        fallbackDiv.innerHTML =
                          '<i class="ti ti-building" style="font-size: 1.2rem; color: #6c757d;"></i>';
                        parent.appendChild(fallbackDiv);
                      }
                    }}
                  />
                ) : (
                  <div className="d-flex align-items-center justify-content-center w-100 h-100 bg-light rounded-circle">
                    <img
                      src={companyLogo}
                      alt="company logo"
                      className="rounded-circle"
                    />
                  </div>
                )}
              </Link>
              <div>
                <h6 className="fs-14">
                  <Link to={createCompanyUrl(company)} className="fw-medium">
                    {company.company_name?.length > 20 ? (
                      <OverlayTrigger
                        placement="top"
                        overlay={<BootstrapTooltip id={`tooltip-${company.company_id}`}>{company.company_name}</BootstrapTooltip>}
                      >
                        <span>{`${company.company_name.substring(0, 20)}...`}</span>
                      </OverlayTrigger>
                    ) : (
                      company.company_name
                    )}
                  </Link>
                </h6>
                <p className="text-default mb-0">
                  {company.industry_type_display_name || "Industry Not Set"}
                </p>
              </div>
            </div>
            <div className="dropdown table-action">
              <Link
                to="#"
                className="action-icon btn btn-icon btn-sm btn-outline-light shadow"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="ti ti-dots-vertical" />
              </Link>
              <div className="dropdown-menu dropdown-menu-right">
                <Link
                  className="dropdown-item"
                  to="#"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#offcanvas_edit"
                  onClick={() => handleEdit(company)}
                >
                  <i className="ti ti-edit text-blue" /> Edit
                </Link>
                <Link
                  className="dropdown-item"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleDelete(company);
                  }}
                >
                  <i className="ti ti-trash text-danger" /> Delete
                </Link>
                <Link
                  className="dropdown-item"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handlePreview(company);
                  }}
                  style={{ pointerEvents: isPreviewing ? "none" : "auto" }}
                >
                  <i
                    className={`ti ${isPreviewing ? "ti-loader ti-spin" : "ti-eye"
                      } text-blue-light`}
                  />{" "}
                  Preview
                </Link>
              </div>
            </div>
          </div>
          <div className="d-block">
            <div className="d-flex flex-column">
              <p className="text-default d-inline-flex align-items-center mb-2">
                <i className="ti ti-phone text-dark me-1" />
                {company.phone || "No phone"}
              </p>
              <p className="text-default d-inline-flex align-items-center mb-2">
                <i className="ti ti-user text-dark me-1" />
                {(typeof company.assigned_user === "object" &&
                  company.assigned_user
                  ? company.assigned_user.full_name
                  : company.assigned_user_full_name) || "Not Assigned"}
              </p>
              <p className="text-default d-inline-flex align-items-center mb-2">
                <i className="ti ti-users text-dark me-1" />
                {company.assigned_team?.team_name || "Not Assigned"}
              </p>
              <p className="text-default d-inline-flex align-items-center mb-2">
                <i className="ti ti-cash text-dark me-1" />
                {company.annual_revenue || "Not Assigned"}{" "}
                {company.currency_code}
              </p>
              <p className="text-default d-inline-flex align-items-center">
                <i className="ti ti-map-pin-pin text-dark me-1" />
                {formatLocation(company.city, company.country) ||
                  "Location not set"}
              </p>
            </div>
          </div>
          <div className="d-flex justify-content-center align-items-center mt-3 pt-3 border-top">
            <ul className="social-info">
              {company.facebook_url ? (
                <li>
                  <Link
                    to={`https://${company.facebook_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i
                      className="fa-brands fa-facebook-f"
                      style={{ color: "#824eeb" }}
                    />
                  </Link>
                </li>
              ) : (
                <li>
                  <Tooltip title="Facebook URL not set">
                    <i
                      className="fa-brands fa-facebook-f mx-2 mb-2 mt-2"
                      style={{ color: "#79787aff" }}
                    />
                  </Tooltip>
                </li>
              )}
              {company.twitter_url ? (
                <li>
                  <Link
                    to={`https://${company.twitter_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i
                      className="fa-brands fa-twitter"
                      style={{ color: "#824eeb" }}
                    />
                  </Link>
                </li>
              ) : (
                <li>
                  <Tooltip title="Twitter URL not set">
                    <i
                      className="fa-brands fa-twitter mx-2 mb-2 mt-2"
                      style={{ color: "#79787aff" }}
                    />
                  </Tooltip>
                </li>
              )}
              {company.linkedin_url ? (
                <li>
                  <Link
                    to={`https://${company.linkedin_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i
                      className="fa-brands fa-linkedin-in"
                      style={{ color: "#824eeb" }}
                    />
                  </Link>
                </li>
              ) : (
                <li>
                  <Tooltip title="Linkedin URL not set">
                    <i
                      className="fa-brands fa-linkedin-in mx-2 mb-2 mt-2"
                      style={{ color: "#79787aff" }}
                    />
                  </Tooltip>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">
                      Companies
                      <span className="count-title">
                        {filters.searchTerm !== "" ||
                          filters.dateRange[0] !== null ||
                          filters.dateRange[1] !== null ||
                          filters.industryFilter.length > 0 ||
                          filters.countryFilter.length > 0
                          ? totalFilteredCount
                          : pagination.totalCount}
                      </span>
                    </h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              {/* /Page Header */}
              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col-sm-4"></div>
                    <div className="col-sm-8">
                      <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                        <ExportComponent
                          entityType="company"
                          filters={{ ...filters, ...additionalFilters }}
                        />
                        <Link
                          to="#"
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#add_company"
                          onClick={() => setIsAddCompanyOpen(true)}
                        >
                          <i className="ti ti-square-rounded-plus me-2" />
                          Add Company
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body d-flex align-items-center justify-content-between flex-wrap row-gap-2">
                  <TableFilters
                    config={filterConfig}
                    data={companies}
                    onFilterChange={handleFilterChange}
                    onApiFilterChange={handleApiFilterChange}
                    onApiCall={handleApiCall}
                    onFiltersReset={handleFiltersReset}
                    tenantId={selectedTenantId || undefined}
                    className="mt-4"
                  />
                  {/* Right Side Icons */}
                  <div className="d-flex align-items-center flex-wrap row-gap-2">
                    {/* View Toggle Icons */}
                    <div className="view-icons">
                      <Link
                        to="#"
                        className={currentView === "list" ? "active" : ""}
                        onClick={(e) => {
                          e.preventDefault();
                          onViewSwitch("list");
                        }}
                      >
                        <i className="ti ti-list-tree" />
                      </Link>
                      <Link
                        to="#"
                        className={currentView === "grid" ? "active" : ""}
                        onClick={(e) => {
                          e.preventDefault();
                          onViewSwitch("grid");
                        }}
                      >
                        <i className="ti ti-grid-dots" />
                      </Link>
                    </div>
                  </div>
                </div>

                {loading ? (
                  <div className="card-body">
                    <div className="text-center py-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div className="mt-2">
                        <small className="text-muted">
                          Loading Companies...
                        </small>
                      </div>
                    </div>
                  </div>
                ) : error ? (
                  <div className="card-body">
                    <div className="alert alert-danger text-center">
                      <i className="ti ti-alert-circle me-2"></i>
                      {error}
                    </div>
                  </div>
                ) : (
                  <div className="card-body">
                    <div style={{ position: "relative" }}>
                      {isFilterLoading && (
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(255, 255, 255, 1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 10,
                            minHeight: "200px",
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <div
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <span>Filtering results...</span>
                          </div>
                        </div>
                      )}
                      {/* Company Grid */}
                      <div className="row contact-grid-row">
                        {displayCompanies.map(renderCompanyCard)}
                      </div>

                      {/* Pagination */}
                      <TablePagination
                        currentPage={pagination.currentPage || currentPage}
                        pageSize={12}
                        totalItems={
                          isFilterActive
                            ? totalFilteredCount
                            : pagination.totalCount
                        }
                        filteredItems={totalFilteredCount}
                        isFiltered={
                          filters.searchTerm !== "" ||
                          filters.dateRange[0] !== null ||
                          filters.dateRange[1] !== null ||
                          filters.industryFilter.length > 0 ||
                          filters.countryFilter.length > 0
                        }
                        onPageChange={handlePageChange}
                        onPageSizeChange={() => { }}
                        entityName="Companies"
                        loading={loading}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Company Offcanvas */}
      <div
        className="offcanvas offcanvas-end company-form-offcanvas"
        tabIndex={-1}
        id="add_company"

      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">Add New Company</h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        {isAddCompanyOpen && (
          <CompanyForm
            mode="add"
            company={null}
            formId="add_company_form_grid"
            onSubmit={handleFormSubmit}
            onCancel={() => { }}
            loading={isSubmitting}
            onSuccess={() => formResetTrigger}
          />
        )}
      </div>

      {/* Edit Company Offcanvas */}
      <div
        className="offcanvas offcanvas-end company-form-offcanvas"
        tabIndex={-1}
        id="offcanvas_edit"

      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">Edit Company</h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        {isEditCompanyOpen && (
          <CompanyForm
            mode="edit"
            company={selectedCompany}
            formId="edit_company_form_grid"
            onSubmit={handleFormSubmit}
            onCancel={() => { }}
            loading={isSubmitting}
          />
        )}
      </div>


    </>
  );
};

export default CompaniesGridView;
